/**
 * Join Features — Esri's Analysis tool of the same name, browser-side.
 *
 * Attaches one layer's attributes to another's and writes the result out as a
 * new layer, rather than just highlighting a selection the way Select By
 * Location does. Two questions decide how a target feature picks up a match:
 *
 *   HOW does it match?      — location (a real geometric relationship) or
 *                              matching field values, like a SQL join.
 *   WHAT if more than one join feature matches the same target feature? —
 *     keep only the first, roll every match into one summary row (with an
 *     always-present match count and any statistics asked for), or keep every
 *     match as its own output row (one-to-many).
 *
 * NOTHING NEW UNDER THE HOOD
 * The spatial half reuses `spatialRelate.ts` exactly as Select By Location
 * does — the same exact predicates (not a bounding-box approximation), the
 * same bbox-narrowed load so a big service is not pulled whole. The
 * attribute half is a plain equality lookup, the same idea `localSource.ts`'s
 * WHERE-clause engine already relies on elsewhere in the app. This file's own
 * job is just wiring those two into "match, then merge, then write a layer" —
 * see `run()`.
 */

import { useEffect, useState } from 'react';
import { Loader2, Plus, X as RemoveIcon } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, FieldRow, LayerSelect, Notice, btnClass, inputClass } from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { computeBounds } from '../../../layers/localFile';
import { fetchLayerFieldNames, fetchNumericFieldNames } from '../../../tools/toolFields';
import { loadFeaturesDetailed } from './featureLoad';
import {
  RELATIONSHIPS,
  bboxUnion,
  degreesForMetres,
  padBbox,
  prepare,
  relate,
  scaleForLatitude,
  worthTesting,
  type SpatialRelationship,
} from '../../../utils/spatialRelate';

const UNIT_METRES: Record<string, number> = { m: 1, km: 1000, ft: 0.3048, mi: 1609.344 };

/** A ceiling, not a target — the work is quadratic for a spatial join, and
 *  this runs in the browser. See Select By Location's identical note. */
const MAX_TARGET_FEATURES = 4000;
const MAX_JOIN_FEATURES = 4000;

type JoinMethod = 'spatial' | 'attribute';
type MatchMode = 'first' | 'summarize' | 'all';
type JoinScope = 'inner' | 'left';
type StatKind = 'sum' | 'mean' | 'min' | 'max';

interface FieldPair {
  id: string;
  targetField: string;
  joinField: string;
}

interface StatEntry {
  id: string;
  field: string;
  statistic: StatKind;
}

let nextRowId = 0;
function newRowId(): string {
  nextRowId += 1;
  return `row-${nextRowId}`;
}

const STAT_LABELS: Record<StatKind, string> = { sum: 'Sum', mean: 'Average', min: 'Minimum', max: 'Maximum' };

function aggregateStat(values: number[], statistic: StatKind): number | null {
  if (values.length === 0) return null;
  switch (statistic) {
    case 'sum':
      return values.reduce((total, value) => total + value, 0);
    case 'mean':
      return values.reduce((total, value) => total + value, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return null;
  }
}

/** Values are compared as trimmed text either side, so `10` and `"10"` match
 *  but `10` and `10.0` do not — an exact-match join, not a fuzzy one. */
function joinKey(properties: Record<string, unknown>, fields: string[]): string {
  return fields.map((field) => String(properties[field] ?? '').trim()).join('\u0001');
}

function compareForSort(a: unknown, b: unknown, direction: 'asc' | 'desc'): number {
  const aNumber = Number(a);
  const bNumber = Number(b);
  const cmp =
    Number.isFinite(aNumber) && Number.isFinite(bNumber)
      ? aNumber - bNumber
      : String(a ?? '').localeCompare(String(b ?? ''));
  return direction === 'desc' ? -cmp : cmp;
}

export function JoinFeaturesPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const defaultRelationship = cfg<SpatialRelationship>(ctx.config, 'defaultRelationship', 'intersect');
  const defaultDistance = cfg<number>(ctx.config, 'defaultDistance', 100);
  const defaultUnit = cfg<string>(ctx.config, 'unit', 'm');

  const [targetId, setTargetId] = useState(allowed[0]?.id ?? '');
  const [joinId, setJoinId] = useState(allowed[1]?.id ?? allowed[0]?.id ?? '');
  const [method, setMethod] = useState<JoinMethod>('spatial');
  const [relationship, setRelationship] = useState<SpatialRelationship>(defaultRelationship);
  const [distance, setDistance] = useState(String(defaultDistance));
  const [unit, setUnit] = useState(defaultUnit);
  const [fieldPairs, setFieldPairs] = useState<FieldPair[]>([
    { id: newRowId(), targetField: '', joinField: '' },
  ]);
  const [matchMode, setMatchMode] = useState<MatchMode>('first');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [statEntries, setStatEntries] = useState<StatEntry[]>([]);
  const [scope, setScope] = useState<JoinScope>('inner');

  const [targetFields, setTargetFields] = useState<string[]>([]);
  const [joinFields, setJoinFields] = useState<string[]>([]);
  const [joinNumericFields, setJoinNumericFields] = useState<string[]>([]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{
    targetFeatures: number;
    matched: number;
    unmatched: number;
    outputFeatures: number;
    cappedTarget: boolean;
    cappedJoin: boolean;
  } | null>(null);
  const [resultLayerId, setResultLayerId] = useState<string | null>(null);

  const target = allowed.find((layer) => layer.id === targetId);
  /* The join layer is drawn from EVERY layer, the same reasoning Select By
     Location uses for its own selecting layer: which layers this tool may
     ENRICH is a permission an admin sets (`allowed`); which layer it may pull
     attributes FROM is only a question of what is already on the map. */
  const joinLayer = ctx.layers.find((layer) => layer.id === joinId);

  const needsDistance = method === 'spatial' && relationship === 'withinDistance';
  const relationshipHelp = RELATIONSHIPS.find((entry) => entry.id === relationship)?.help ?? '';
  const sameLayer = Boolean(targetId) && targetId === joinId;

  useEffect(() => {
    setTargetFields([]);
    if (!target) return undefined;
    let live = true;
    void fetchLayerFieldNames(target, ctx.module).then((names) => {
      if (live) setTargetFields([...names].sort());
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  useEffect(() => {
    setJoinFields([]);
    setJoinNumericFields([]);
    if (!joinLayer) return undefined;
    let live = true;
    void fetchLayerFieldNames(joinLayer, ctx.module).then((names) => {
      if (live) setJoinFields([...names].sort());
    });
    void fetchNumericFieldNames(joinLayer, ctx.module).then((names) => {
      if (live) setJoinNumericFields([...names].sort());
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinLayer?.id]);

  // A result already on the map from a previous Run in this session updates in
  // place instead of stacking a fresh layer per click — see Density's own
  // identical note on why that is what makes tweaking a setting and re-running
  // feel like adjusting, not repeatedly creating things to clean up after.
  const resultStillOnMap = Boolean(resultLayerId) && ctx.layers.some((layer) => layer.id === resultLayerId);

  const addFieldPair = () =>
    setFieldPairs((rows) => [...rows, { id: newRowId(), targetField: '', joinField: '' }]);
  const updateFieldPair = (id: string, changes: Partial<FieldPair>) =>
    setFieldPairs((rows) => rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  const removeFieldPair = (id: string) =>
    setFieldPairs((rows) => (rows.length > 1 ? rows.filter((row) => row.id !== id) : rows));

  const addStatEntry = () =>
    setStatEntries((rows) => [...rows, { id: newRowId(), field: joinNumericFields[0] ?? '', statistic: 'sum' }]);
  const updateStatEntry = (id: string, changes: Partial<StatEntry>) =>
    setStatEntries((rows) => rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
  const removeStatEntry = (id: string) => setStatEntries((rows) => rows.filter((row) => row.id !== id));

  const run = async () => {
    if (!target || !joinLayer) return;

    const usableFieldPairs = fieldPairs.filter((pair) => pair.targetField && pair.joinField);
    if (method === 'attribute' && usableFieldPairs.length === 0) {
      setError('Choose at least one pair of fields to match values on.');
      return;
    }

    const metres = Number(distance) * (UNIT_METRES[unit] ?? 1);
    if (needsDistance && (!Number.isFinite(metres) || metres <= 0)) {
      setError('Enter a search distance greater than zero.');
      return;
    }

    setError(null);
    setBusy(true);
    setSummary(null);

    try {
      const targetLoad = await loadFeaturesDetailed(target, ctx, { limit: MAX_TARGET_FEATURES });
      const targetFeatures = targetLoad.features;
      if (targetFeatures.length === 0) {
        setError(`${getLayerDisplayName(target)} returned no features to enrich.`);
        return;
      }

      let joinFeatures: GeoJSON.Feature[];
      let cappedJoin = false;
      /* One match list per target feature, holding indexes into `joinFeatures`
         — built one of two totally different ways below, but everything after
         this point (merging, summarizing, writing the output) does not care
         which. */
      let matchesFor: number[][];

      if (method === 'spatial') {
        const preparedTarget = targetFeatures.map((feature) => prepare(feature.geometry));
        const extent = bboxUnion(preparedTarget.map((geometry) => geometry.bbox));
        const scaleX = scaleForLatitude(extent);
        const pad = needsDistance && extent ? degreesForMetres(metres, (extent.minY + extent.maxY) / 2) : null;
        const searchBox = extent && pad ? padBbox(extent, pad.x, pad.y) : extent;

        const joinLoad = await loadFeaturesDetailed(joinLayer, ctx, {
          limit: MAX_JOIN_FEATURES,
          bbox: searchBox ?? undefined,
        });
        joinFeatures = joinLoad.features;
        cappedJoin = joinLoad.truncated;
        const preparedJoin = joinFeatures.map((feature) => prepare(feature.geometry));

        if (relationship === 'within' && !preparedJoin.some((geometry) => geometry.polygons.length > 0)) {
          setError(
            `"Completely within" needs ${getLayerDisplayName(joinLayer)} — the layer attributes come FROM — to be polygons. Try Intersect or a search distance instead.`,
          );
          return;
        }
        if (relationship === 'contains' && !preparedTarget.some((geometry) => geometry.polygons.length > 0)) {
          setError(
            `"Completely contain" needs ${getLayerDisplayName(target)} to be polygons — only an area can enclose something.`,
          );
          return;
        }

        const padX = pad?.x ?? 0;
        const padY = pad?.y ?? 0;
        matchesFor = preparedTarget.map((targetGeometry) => {
          if (targetGeometry.positions.length === 0) return [];
          const hits: number[] = [];
          preparedJoin.forEach((joinGeometry, joinIndex) => {
            if (joinGeometry.positions.length === 0) return;
            if (
              worthTesting(targetGeometry, joinGeometry, relationship, padX, padY) &&
              relate(targetGeometry, joinGeometry, relationship, metres, scaleX)
            ) {
              hits.push(joinIndex);
            }
          });
          return hits;
        });
      } else {
        const joinLoad = await loadFeaturesDetailed(joinLayer, ctx, { limit: MAX_JOIN_FEATURES });
        joinFeatures = joinLoad.features;
        cappedJoin = joinLoad.truncated;

        const joinFieldNames = usableFieldPairs.map((pair) => pair.joinField);
        const lookup = new Map<string, number[]>();
        joinFeatures.forEach((feature, index) => {
          const key = joinKey((feature.properties ?? {}) as Record<string, unknown>, joinFieldNames);
          const bucket = lookup.get(key);
          if (bucket) bucket.push(index);
          else lookup.set(key, [index]);
        });

        const targetFieldNames = usableFieldPairs.map((pair) => pair.targetField);
        matchesFor = targetFeatures.map((feature) => {
          const key = joinKey((feature.properties ?? {}) as Record<string, unknown>, targetFieldNames);
          return lookup.get(key) ?? [];
        });
      }

      if (joinFeatures.length === 0) {
        setError(`${getLayerDisplayName(joinLayer)} returned no features to join from.`);
        return;
      }

      /* Every field name the join layer's loaded features carry, so a field
         that collides with one the target already has can be renamed the same
         way on EVERY output row, whether or not this particular row's match
         happened to carry a value for it. */
      const joinFieldNamesAll = Array.from(
        new Set(joinFeatures.flatMap((feature) => Object.keys(feature.properties ?? {}))),
      );
      const targetFieldNamesAll = new Set(
        targetFeatures.flatMap((feature) => Object.keys(feature.properties ?? {})),
      );
      const renameJoinField = (key: string) => (targetFieldNamesAll.has(key) ? `${key}_join` : key);

      const mergeRow = (feature: GeoJSON.Feature, joinProperties: Record<string, unknown>): GeoJSON.Feature => {
        const merged: Record<string, unknown> = { ...(feature.properties ?? {}) };
        for (const [key, value] of Object.entries(joinProperties)) {
          merged[renameJoinField(key)] = value;
        }
        return { type: 'Feature', geometry: feature.geometry, properties: merged };
      };

      const blankJoinProperties = (): Record<string, unknown> => {
        if (matchMode === 'summarize') {
          const properties: Record<string, unknown> = { JOIN_COUNT: 0 };
          for (const stat of statEntries) {
            if (stat.field) properties[renameJoinField(`${stat.field}_${stat.statistic}`)] = null;
          }
          return properties;
        }
        const properties: Record<string, unknown> = {};
        for (const key of joinFieldNamesAll) properties[renameJoinField(key)] = null;
        return properties;
      };

      const outFeatures: GeoJSON.Feature[] = [];
      let matched = 0;
      let unmatched = 0;

      targetFeatures.forEach((feature, targetIndex) => {
        const hits = matchesFor[targetIndex] ?? [];
        if (hits.length === 0) {
          unmatched += 1;
          if (scope === 'left') {
            outFeatures.push({
              type: 'Feature',
              geometry: feature.geometry,
              properties: { ...(feature.properties ?? {}), ...blankJoinProperties() },
            });
          }
          return;
        }
        matched += 1;

        if (matchMode === 'all') {
          for (const hitIndex of hits) {
            outFeatures.push(mergeRow(feature, (joinFeatures[hitIndex].properties ?? {}) as Record<string, unknown>));
          }
        } else if (matchMode === 'first') {
          let chosenIndex = hits[0];
          if (sortField) {
            const sorted = [...hits].sort((a, b) =>
              compareForSort(joinFeatures[a].properties?.[sortField], joinFeatures[b].properties?.[sortField], sortDir),
            );
            chosenIndex = sorted[0];
          }
          outFeatures.push(mergeRow(feature, (joinFeatures[chosenIndex].properties ?? {}) as Record<string, unknown>));
        } else {
          const summaryProperties: Record<string, unknown> = { JOIN_COUNT: hits.length };
          for (const stat of statEntries) {
            if (!stat.field) continue;
            const values = hits
              .map((hitIndex) => Number(joinFeatures[hitIndex].properties?.[stat.field]))
              .filter((value) => Number.isFinite(value));
            summaryProperties[renameJoinField(`${stat.field}_${stat.statistic}`)] = aggregateStat(
              values,
              stat.statistic,
            );
          }
          outFeatures.push({
            type: 'Feature',
            geometry: feature.geometry,
            properties: { ...(feature.properties ?? {}), ...summaryProperties },
          });
        }
      });

      if (outFeatures.length === 0) {
        setError(
          scope === 'inner'
            ? 'Nothing matched, so an inner join has nothing to write. Try Left join to keep every feature, or a looser relationship.'
            : 'No output features were produced.',
        );
        return;
      }

      const geojson: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: outFeatures };
      const name = `Join — ${getLayerDisplayName(target)} + ${getLayerDisplayName(joinLayer)}`;

      if (resultStillOnMap && resultLayerId && ctx.onLayerChange) {
        ctx.onLayerChange(resultLayerId, { localData: geojson, bounds: computeBounds(geojson) });
      } else {
        const newId = ctx.onCreateTempLayer?.(name, geojson, 'Join features');
        setResultLayerId(newId ?? null);
      }

      setSummary({
        targetFeatures: targetFeatures.length,
        matched,
        unmatched,
        outputFeatures: outFeatures.length,
        cappedTarget: targetLoad.truncated,
        cappedJoin,
      });
    } catch (failure) {
      console.error('[joinFeatures] failed', failure);
      setError('Could not complete that join. One of the layers did not answer.');
    } finally {
      setBusy(false);
    }
  };

  /* Clears the PANEL, not the map — the result is a layer now, removed from
     the Layers tab like anything else (see Density's identical note). */
  const clearPanel = () => {
    setSummary(null);
    setError(null);
    setResultLayerId(null);
  };

  return (
    <>
      <ToolPanelHeader
        title="Join features"
        description="Attach one layer's attributes to another, by location or matching values."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>This map has no layers this tool is allowed to use yet.</EmptyHint>
      ) : (
        <>
          <Field label="Add attributes to">
            <LayerSelect layers={allowed} value={targetId} onChange={setTargetId} />
          </Field>

          <Field label="Match by">
            <select
              value={method}
              onChange={(event) => setMethod(event.target.value as JoinMethod)}
              className={inputClass}
            >
              <option value="spatial">Location — a spatial relationship</option>
              <option value="attribute">Attribute values — matching fields</option>
            </select>
          </Field>

          <Field label="From">
            <LayerSelect layers={ctx.layers} value={joinId} onChange={setJoinId} />
          </Field>

          {method === 'spatial' ? (
            <>
              <Field label="Where the target feature">
                <select
                  value={relationship}
                  onChange={(event) => setRelationship(event.target.value as SpatialRelationship)}
                  className={inputClass}
                >
                  {RELATIONSHIPS.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </Field>

              {needsDistance && (
                <FieldRow>
                  <Field label="Search distance">
                    <input
                      type="number"
                      min={0}
                      value={distance}
                      onChange={(event) => setDistance(event.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Unit">
                    <select value={unit} onChange={(event) => setUnit(event.target.value)} className={inputClass}>
                      <option value="m">Metres</option>
                      <option value="km">Kilometres</option>
                      <option value="ft">Feet</option>
                      <option value="mi">Miles</option>
                    </select>
                  </Field>
                </FieldRow>
              )}

              <p className="text-[10.5px] leading-snug text-slate-400">{relationshipHelp}</p>
            </>
          ) : (
            <Field
              label="Matching fields"
              hint="A target feature picks up a join feature's attributes when every pair here has the same value. Add more than one pair for a compound key."
            >
              <div className="space-y-1.5">
                {fieldPairs.map((pair) => (
                  <div key={pair.id} className="flex items-center gap-1.5">
                    <select
                      value={pair.targetField}
                      onChange={(event) => updateFieldPair(pair.id, { targetField: event.target.value })}
                      className={`${inputClass} flex-1`}
                    >
                      <option value="">Target field…</option>
                      {targetFields.map((field) => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                    <span className="flex-shrink-0 text-[11px] text-slate-400">=</span>
                    <select
                      value={pair.joinField}
                      onChange={(event) => updateFieldPair(pair.id, { joinField: event.target.value })}
                      className={`${inputClass} flex-1`}
                    >
                      <option value="">Join field…</option>
                      {joinFields.map((field) => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeFieldPair(pair.id)}
                      disabled={fieldPairs.length === 1}
                      aria-label="Remove field pair"
                      className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                    >
                      <RemoveIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFieldPair}
                  className="flex items-center gap-1 text-[11.5px] font-medium text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Add field pair
                </button>
              </div>
            </Field>
          )}

          {sameLayer && (
            <Notice tone="warn">
              Both layers are the same, so every feature will match itself.
            </Notice>
          )}

          <Field label="When a target feature matches more than one">
            <select
              value={matchMode}
              onChange={(event) => setMatchMode(event.target.value as MatchMode)}
              className={inputClass}
            >
              <option value="first">Keep only the first match</option>
              <option value="summarize">Summarize the matches</option>
              <option value="all">Keep every match, as its own row</option>
            </select>
          </Field>

          {matchMode === 'first' && (
            <FieldRow>
              <Field label="Sort by" hint="Decides which match is 'first'. Leave unset for an arbitrary but consistent pick.">
                <select value={sortField} onChange={(event) => setSortField(event.target.value)} className={inputClass}>
                  <option value="">Unsorted</option>
                  {joinFields.map((field) => (
                    <option key={field} value={field}>
                      {field}
                    </option>
                  ))}
                </select>
              </Field>
              {sortField && (
                <Field label="Direction">
                  <select
                    value={sortDir}
                    onChange={(event) => setSortDir(event.target.value as 'asc' | 'desc')}
                    className={inputClass}
                  >
                    <option value="asc">Lowest / A first</option>
                    <option value="desc">Highest / Z first</option>
                  </select>
                </Field>
              )}
            </FieldRow>
          )}

          {matchMode === 'summarize' && (
            <Field
              label="Statistics"
              hint="A match count is always added. Add a statistic for a numeric field to summarize it too — e.g. the total load on every meter within the ward."
            >
              <div className="space-y-1.5">
                {statEntries.map((stat) => (
                  <div key={stat.id} className="flex items-center gap-1.5">
                    <select
                      value={stat.statistic}
                      onChange={(event) => updateStatEntry(stat.id, { statistic: event.target.value as StatKind })}
                      className={inputClass}
                      style={{ maxWidth: '38%' }}
                    >
                      {(Object.keys(STAT_LABELS) as StatKind[]).map((key) => (
                        <option key={key} value={key}>
                          {STAT_LABELS[key]}
                        </option>
                      ))}
                    </select>
                    <select
                      value={stat.field}
                      onChange={(event) => updateStatEntry(stat.id, { field: event.target.value })}
                      className={`${inputClass} flex-1`}
                    >
                      <option value="">Choose a numeric field…</option>
                      {joinNumericFields.map((field) => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeStatEntry(stat.id)}
                      aria-label="Remove statistic"
                      className="flex-shrink-0 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    >
                      <RemoveIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStatEntry}
                  disabled={joinNumericFields.length === 0}
                  className="flex items-center gap-1 text-[11.5px] font-medium text-blue-600 hover:text-blue-700 disabled:opacity-40"
                >
                  <Plus className="h-3.5 w-3.5" /> Add statistic
                </button>
              </div>
            </Field>
          )}

          <Field
            label="Output"
            hint={
              scope === 'inner'
                ? 'Only target features with at least one match are written out.'
                : 'Every target feature is written out; one with no match gets blank joined fields.'
            }
          >
            <select value={scope} onChange={(event) => setScope(event.target.value as JoinScope)} className={inputClass}>
              <option value="inner">Inner join — matched features only</option>
              <option value="left">Left join — keep every feature</option>
            </select>
          </Field>

          <ToolActions>
            <button
              type="button"
              onClick={() => void run()}
              disabled={!target || !joinLayer || busy}
              className={`${btnClass} gl-btn--primary`}
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {busy ? 'Joining…' : resultStillOnMap ? 'Update' : 'Run'}
            </button>
            <button type="button" onClick={clearPanel} disabled={!summary} className={`${btnClass} gl-btn--secondary`}>
              Reset
            </button>
          </ToolActions>

          {error && <Notice tone="warn">{error}</Notice>}

          {summary && (
            <Notice>
              {summary.outputFeatures.toLocaleString()} feature{summary.outputFeatures === 1 ? '' : 's'} written —{' '}
              {summary.matched.toLocaleString()} of {summary.targetFeatures.toLocaleString()} target feature
              {summary.targetFeatures === 1 ? '' : 's'} matched
              {summary.unmatched > 0 && scope === 'left' ? `, ${summary.unmatched.toLocaleString()} kept unmatched` : ''}
              {summary.unmatched > 0 && scope === 'inner' ? `, ${summary.unmatched.toLocaleString()} dropped (no match)` : ''}
              .{(summary.cappedTarget || summary.cappedJoin) && (
                <>
                  {' '}
                  {summary.cappedTarget ? 'The target layer' : 'The layer attributes came from'} had more features than
                  this could read — the answer above is a floor, not a total.
                </>
              )}
              {' '}Added to the Layers tab as a temporary layer, with its own attribute table. It stays until you
              remove it, and nobody else sees it.
            </Notice>
          )}

          <p className="text-[10.5px] leading-snug text-slate-400">
            A spatial match is exact — the same geometry test Select By Location uses, not a bounding-box guess.
            An attribute match compares field values as exact text, so "10" and "10.0" are not the same value. A
            field name that exists on both layers is kept from the target layer as-is; the join layer's own copy
            is added as "name_join".
          </p>
        </>
      )}
    </>
  );
}

export default JoinFeaturesPanel;
